import {
	Button,
	Checkbox,
	DatePicker,
	Form,
	Input,
	Select,
	Space,
	Spin,
	Switch,
	Tabs,
} from "antd";

import { LeftOutlined, SaveOutlined } from "@ant-design/icons";

import { t } from "@lingui/macro";
import { RangePickerProps } from "antd/lib/date-picker";
import UploadSingleImage from "components/upload-single-image/UploadSingleImage";
import { languages } from "constants/constants";
import { APP_PATH } from "constants/path";
import { GOTO_OPTIONS } from "constants/survey";
import QuestionConfig, {
	QuestionConfigRefProps,
	isSingleChoiceQuestion,
} from "features/survey/detail/components/QuestionConfig";
import {
	requestApiCreateSurvey,
	requestApiGetSurveyDetail,
	requestApiUpdateSurvey,
} from "features/survey/services/RequestApis";
import { NextStepSetting, Question } from "features/survey/type";
import CheckAccess from "hoc/CheckAccess";
import useQuery from "hooks/useQuery";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { translate } from "utils";
import {
	formatDateStringMinusLocalTmeYMD,
	getRangeDateQuery,
} from "utils/format";
import { checkTimeSurveyEnabled } from "utils/surveyUtils";

interface Props {
	id: string;
}

const OPEN_SETTING = {
	openApp: "openApp",
	usedTime: "usedTime",
};

const DEFAULT_START_TIME_REGEX = /T00:00:00/;
const DEFAULT_END_TIME_REGEX = /T23:59:00/;

const { Option } = Select;
const { RangePicker } = DatePicker;

const MainInformation = ({ id }: Props) => {
	const [isLoading, setIsLoading] = useState(false);
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const [surveyDetail, setSurveyDetail] = useState<any>();
	const { pathname } = useLocation();
	const [activeKey, setActiveKey] = useState("");
	const [dates, onChangeDates] = useState(["" as any, "" as any]);
	const [status, onChangeStatus] = useState(true);
	const query = useQuery();
	const questionConfigRef = useRef<QuestionConfigRefProps | null>(null);
	const surveyType = Form.useWatch("type", form);
	const settingOpen = Form.useWatch("settingOpen", form);

	useEffect(() => {
		const tab = query.get("language");
		setActiveKey(tab || languages[0].key);
		getSurveyDetail();
	}, []);

	const getSurveyDetail = async () => {
		form.setFieldValue("status", true);
		if (!id) return;
		setIsLoading(true);
		const data = await requestApiGetSurveyDetail(id);
		if (data) {
			setSurveyInfo(data);
		}
		setIsLoading(false);
	};

	const setSurveyInfo = (survey: any) => {
		setSurveyDetail(survey);
		form.setFieldValue("image", survey?.image);
		form.setFieldValue("name", survey?.name);
		form.setFieldValue("link", survey?.link);
		form.setFieldValue("description", survey?.description);
		form.setFieldValue("startDate", survey?.startDate);
		form.setFieldValue("endDate", survey?.endDate);
		form.setFieldValue("status", survey?.status === "enabled" ? true : false);
		form.setFieldValue("type", survey?.type || "link");
		form.setFieldValue("rules", survey?.rules);
		if (survey?.rules[0].openApp) {
			form.setFieldValue("settingOpen", OPEN_SETTING.openApp);
		} else if (survey?.rules[0].usedTime) {
			form.setFieldValue("settingOpen", OPEN_SETTING.usedTime);
		}
		form.setFieldValue("platform", survey?.platform);
		// format questions of the payload from response
		const questions = survey?.questions
			?.map((question: Question) => {
				const newQuestion = { ...question };
				if (!isSingleChoiceQuestion(question)) {
					return {
						...newQuestion,
						goToOption: getGoToOptionFromSetting(question.setting),
					};
				}
				const options = question.options!.map(option => {
					return {
						...option,
						goToOption: getGoToOptionFromSetting(option.setting),
					};
				});
				return { ...question, options };
			})
			.sort((a: Question, b: Question) => a.no - b.no);

		form.setFieldValue("questions", questions || []);

		onChangeStatus(survey?.status === "enabled" ? true : false);
		onChangeDates([moment(survey?.startDate), moment(survey?.endDate)]);
		form.setFieldValue("dates", [
			moment(formatDateStringMinusLocalTmeYMD(survey?.startDate)),
			moment(formatDateStringMinusLocalTmeYMD(survey?.endDate)),
		]);
	};

	const handleTabChanged = (key: string) => {
		let path = `${pathname}?language=${key}`;
		if (query.get("tab")) {
			path += `&tab=${query.get("tab")}`;
		}
		navigate(path);
		setActiveKey(key);
	};

	const getSettingFromStr = (value: string) => {
		if (!value)
			return {
				type: GOTO_OPTIONS[0].value,
			};
		if (value.includes("Q_")) {
			return {
				type: GOTO_OPTIONS[2].value,
				nextStep: Number(value.split("Q_")[1]),
			};
		}
		return {
			type: value,
		};
	};

	const getGoToOptionFromSetting = (setting?: NextStepSetting) => {
		if (!setting) return "";
		if (setting.type === GOTO_OPTIONS[2].value) {
			return "Q_" + setting.nextStep;
		}
		return setting.type;
	};

	// Format the questions of payload before sending it to BE
	const formatQuestions = (payload: {
		questions?: Question[];
		[key: string]: any;
	}) => {
		if (!payload.questions) return payload;

		payload.questions = payload.questions.map((question, index) => {
			// if the question is not the single-choice type
			if (!isSingleChoiceQuestion(question!)) {
				const newQuestion = {
					...question,
					setting: getSettingFromStr(question.goToOption),
					no: question.no || index + 1,
				};
				return newQuestion;
			}

			// update setting of the single-choice question
			const options = question.options!.map(option => {
				const newOption = { ...option };
				newOption.setting = getSettingFromStr(newOption.goToOption);
				return newOption;
			});
			return {
				...question,
				no: index + 1,
				options,
				nextStep: index + 1,
			};
		});
		return payload;
	};

	const handelSubmitted = async (payload: any) => {
		setIsLoading(true);
		payload = formatQuestions(payload);

		const activeDate = form.getFieldValue("startDate");
		const endDate = form.getFieldValue("endDate");
		const dateAt = new Date(activeDate);
		const dateEn = new Date(endDate);

		if (!DEFAULT_START_TIME_REGEX.test(activeDate)) {
			dateAt.setDate(dateAt.getDate() + 1);
		}

		if (!DEFAULT_END_TIME_REGEX.test(endDate)) {
			dateEn.setDate(dateEn.getDate() + 1);
		}

		const { start, end } = getRangeDateQuery(dateAt, dateEn);

		if (settingOpen === OPEN_SETTING.openApp) {
			delete payload.rules[0].usedTime;
		} else if (settingOpen === OPEN_SETTING.usedTime) {
			delete payload.rules[0].openApp;
		}
		const params = {
			...payload,
			startDate: start,
			endDate: end,
			status: payload.status ? "enabled" : "disabled",
		};
		if (id) {
			await requestApiUpdateSurvey(params, id);
		} else {
			await requestApiCreateSurvey(params);
			navigate(APP_PATH.survey);
		}
		setIsLoading(false);
	};

	const handleImageChanged = (url = "") => {
		form.setFieldValue("image", url);
	};

	const handleImageBeforeUploaded = (message: string) => {
		if (message) {
			form.setFields([
				{
					name: "image",
					errors: [message],
				},
			]);
		}
	};
	const disabledDate: RangePickerProps["disabledDate"] = current =>
		current && current < moment().subtract(1, "day").endOf("day");

	const options = [
		{ label: translate("Customer App"), value: "customerApp", disabled: true },
		{ label: translate("Customer Web"), value: "customerWeb", disabled: true },
		{ label: translate("Merchant App"), value: "merchantApp", disabled: true },
	];

	const showTabErrored = () => {
		form
			.validateFields()
			.then()
			.catch(error => {
				console.log(error);
				for (const err of error.errorFields) {
					if (err.name[0] === "questions") return;
					navigate(`${pathname}?language=${err.name[1] ?? "en"}`);
					setActiveKey(err.name[1]);
					return;
				}
			});
	};

	const handleClickSave = () => {
		showTabErrored();
		questionConfigRef.current?.showErrorTab();
	};

	return (
		<Spin spinning={isLoading}>
			<Form
				form={form}
				name="register"
				onFinish={handelSubmitted}
				layout="inline"
				scrollToFirstError
			>
				<Space direction="vertical" size="middle" className="flex w-full">
					<div className="shadow-lg px-5 pt-5 pb-10 mb-10 bg-white p-5">
						<div className="mt-3 flex justify-between">
							<label className="font-semibold text-lg">{t`Main information`}</label>
						</div>
						<div className="flex">
							<div className="flex w-1/2">
								<div className="p-5">
									<p className="font-semibold mb-2">
										{translate("Image")}
										<span className="text-red-500">*</span>
									</p>
									<Form.Item shouldUpdate>
										{() => (
											<Form.Item
												name="image"
												label={translate("Image")}
												rules={[{ required: true }]}
											>
												<UploadSingleImage
													onChange={handleImageChanged}
													beforeUpload={handleImageBeforeUploaded}
													imageUrl={form.getFieldValue("image") || ""}
													folderUpload="news"
													className="w-24 subscription-upload-image"
													disabled={
														id
															? checkTimeSurveyEnabled(surveyDetail?.endDate)
															: false
													}
												/>
											</Form.Item>
										)}
									</Form.Item>
								</div>
								<div className="p-2 mt-8 items-center">
									<p className="text-gray-400">
										{translate(
											"Recommended resolution is 48*48 with file size less than 2MB, keep visual elements centered",
										)}
									</p>
								</div>
							</div>
							<Tabs
								onChange={handleTabChanged}
								activeKey={activeKey}
								className="bg-white p-5 w-1/2"
								items={languages.map(lang => {
									return {
										label: translate(lang.nameE),
										key: lang.key,
										forceRender: true,
										children: (
											<>
												<div>
													<span>{translate("Survey name")}</span>
													<span className="text-red-500">*</span>
													<Form.Item
														name={["name", lang.key]}
														label={translate("Survey name*")}
														rules={[
															{
																required: true,
																message: translate("Name is required"),
															},
														]}
														className="mt-2"
													>
														<Input
															placeholder={translate("Survey name")}
															disabled={
																id
																	? checkTimeSurveyEnabled(
																			surveyDetail?.endDate,
																	  )
																	: false
															}
														/>
													</Form.Item>
												</div>
												<div className="mt-2">
													<span>{translate("Type")}</span>
													<span className="text-red-500">*</span>
													<Form.Item
														name={"type"}
														rules={[
															{
																required: true,
																message: translate("This field is required"),
															},
														]}
													>
														<Select>
															<Option value="link">{translate("Link")}</Option>
															<Option value="custom">
																{translate("Custom")}
															</Option>
														</Select>
													</Form.Item>
												</div>
												{surveyType === "link" && (
													<div className="mt-5">
														<span>{translate("Link")}</span>
														<span className="text-red-500">*</span>
														<Form.Item
															name={["link", lang.key]}
															label={translate("Link")}
															rules={[
																{
																	required: true,
																	message: translate("Link is required"),
																},
															]}
															className="mt-2"
														>
															<Input
																placeholder={translate("Link")}
																disabled={
																	id
																		? checkTimeSurveyEnabled(
																				surveyDetail?.endDate,
																		  )
																		: false
																}
															/>
														</Form.Item>
													</div>
												)}
												<div className="mt-5">
													<span>{translate("Description")}</span>
													<span className="text-red-500">*</span>
													<Form.Item
														name={["description", lang.key]}
														label={translate("Description")}
														rules={[
															{
																required: true,
																message: translate("Description is required"),
															},
														]}
														className="mt-2"
													>
														<Input.TextArea
															rows={4}
															disabled={checkTimeSurveyEnabled(
																surveyDetail?.endDate,
															)}
															placeholder={translate("Enter your description")}
														/>
													</Form.Item>
												</div>
											</>
										),
									};
								})}
							/>
						</div>
					</div>
					<div className="shadow-lg px-5 pt-5 pb-10 mb-10 bg-white p-5">
						<p className="font-semibold text-lg mb-4">{t`Setting`}</p>
						<div className="grid gap-4">
							<div>
								<p>
									<span>{translate("Active date")}</span>
									<span className="text-red-500">*</span>
								</p>
								<Form.Item
									name={["dates"]}
									label={translate("Active date*")}
									rules={[
										{
											required: true,
											message: translate("This field is required"),
										},
									]}
									className="mt-2"
								>
									<RangePicker
										className="w-1/2 mt-2"
										value={dates as any}
										format="YYYY-MM-DD"
										disabled={
											id ? checkTimeSurveyEnabled(surveyDetail?.endDate) : false
										}
										disabledDate={disabledDate}
										allowClear
										onChange={values => {
											onChangeDates(values as any);
											form.setFieldValue(
												"startDate",
												values?.[0]?.startOf("day")?.toISOString(),
											);
											form.setFieldValue(
												"endDate",
												values?.[1]?.startOf("day")?.toISOString(),
											);
										}}
									/>
								</Form.Item>
							</div>
							<div className="">
								<div className="flex flex-col">
									<span>{translate("Platform")}</span>
								</div>
								<div className="flex gap-6 items-center">
									<div>
										<span>{translate("Display when user")}</span>
									</div>
									<div className="flex gap-6">
										<div>
											<Form.Item
												name={["settingOpen"]}
												initialValue={OPEN_SETTING.openApp}
											>
												<Select
													onChange={value => {
														form.setFieldValue("settingOpen", value);
													}}
												>
													<Option value={OPEN_SETTING.openApp}>
														{translate("Open App")}
													</Option>
													<Option value={OPEN_SETTING.usedTime}>
														<span className="capitalize">
															{translate("Used time")}
														</span>
													</Option>
												</Select>
											</Form.Item>
										</div>
										{settingOpen === OPEN_SETTING.openApp && (
											<div>
												<Form.Item name={["rules", 0, "openApp"]}>
													<Input
														suffix={
															<div className="border-l pl-2 capitalize">
																{translate("times")}
															</div>
														}
													/>
												</Form.Item>
											</div>
										)}
										{settingOpen === OPEN_SETTING.usedTime && (
											<div>
												<Form.Item name={["rules", 0, "usedTime"]}>
													<Input
														suffix={
															<div className="border-l pl-2">
																<span className="capitalize">
																	{translate("seconds")}
																</span>
															</div>
														}
													/>
												</Form.Item>
											</div>
										)}
									</div>
								</div>
							</div>
							<div>
								<div>
									<span>{translate("Platform")}</span>
								</div>
								<div className="flex gap-4">
									<div>
										<Form.Item
											name={["platform", "customerApp"]}
											valuePropName="checked"
										>
											<Checkbox>{translate("Customer App")}</Checkbox>
										</Form.Item>
									</div>
									<div>
										<Form.Item
											name={["platform", "customerWeb"]}
											valuePropName="checked"
										>
											<Checkbox>{translate("Customer Web")}</Checkbox>
										</Form.Item>
									</div>
									<div>
										<Form.Item
											name={["platform", "merchantApp"]}
											valuePropName="checked"
										>
											<Checkbox>{translate("Merchant App")}</Checkbox>
										</Form.Item>
									</div>
								</div>
							</div>
							<div>
								<span>{translate("Status")}</span>
								<Form.Item
									name="status"
									label={translate("Status")}
									className="news-form-label"
								>
									<Switch
										checkedChildren={t`Enable`}
										unCheckedChildren={t`Disable`}
										checked={
											checkTimeSurveyEnabled(surveyDetail?.endDate)
												? false
												: status
										}
										disabled={
											id ? checkTimeSurveyEnabled(surveyDetail?.endDate) : false
										}
										onChange={value => {
											form.setFieldValue("status", value);
											onChangeStatus(value);
										}}
									/>
								</Form.Item>
							</div>
						</div>
					</div>

					{surveyType === "custom" && (
						<QuestionConfig form={form} _ref={questionConfigRef} />
					)}

					<div className="mt-5 flex justify-center sm:justify-end items-center">
						<Link to={APP_PATH.survey}>
							<Button
								icon={<LeftOutlined />}
								type="primary"
								className="flex items-center bg-[#595959] focus:bg-[#8c8c8c] active:bg-[#8c8c8c] hover:bg-[#8c8c8c] border-0  mr-2"
							>
								{t`Go back`}
							</Button>
						</Link>
						{!id ? (
							<Button
								type="primary"
								className="bg-[#84AD2B] hover:bg-[#84AD2B] focus:bg-[#84AD2B] flex items-center rounded border-0"
								icon={<SaveOutlined />}
								htmlType="submit"
								onClick={() => handleClickSave()}
							>
								{translate("Create")}
							</Button>
						) : !checkTimeSurveyEnabled(surveyDetail?.endDate) ? (
							<CheckAccess perms={["update_popup"]}>
								<Button
									htmlType="submit"
									icon={<SaveOutlined />}
									type="primary"
									className="flex items-center bg-[#1890ff]"
									onClick={() => handleClickSave()}
								>
									{t`Save`}
								</Button>
							</CheckAccess>
						) : null}
					</div>
				</Space>
			</Form>
		</Spin>
	);
};

export default MainInformation;
